/**
 * Claim-session requests (§11.1 step 1, §17.2 CSRF): the claim session is an HttpOnly cookie, so cookie-authenticated
 * mutations carry the double-submit `x-csrf` header read from the readable `windrop_csrf` cookie. Reads use the
 * plain wrapper. Evidence bytes go straight to the presigned URL (never through the JSON client).
 */
import type { ProblemDetails } from '@windrop/contracts';
import { apiUrl } from '../config';
import { IDEMPOTENCY_KEY_HEADER, PROBLEM_CONTENT_TYPE } from './constants';
import { ApiError, NetworkError, type RequestOptions } from './types';

export const CSRF_COOKIE = 'windrop_csrf';
export const CSRF_HEADER = 'x-csrf';

export function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const m = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${CSRF_COOKIE}=`));
  return m ? decodeURIComponent(m.slice(CSRF_COOKIE.length + 1)) : null;
}

export async function claimMutation<T>(
  path: string,
  body: unknown,
  opts: RequestOptions & { method?: 'POST' | 'PUT' } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', 'Content-Type': 'application/json' };
  if (opts.idempotencyKey) headers[IDEMPOTENCY_KEY_HEADER] = opts.idempotencyKey;
  const csrf = readCsrfCookie();
  if (csrf) headers[CSRF_HEADER] = csrf;
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: opts.method ?? 'POST',
      headers,
      body: JSON.stringify(body ?? {}),
      credentials: 'include',
      signal: opts.signal,
    });
  } catch (cause) {
    throw new NetworkError('network_unavailable', cause);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  if (!res.ok) {
    const parsed =
      ct.includes(PROBLEM_CONTENT_TYPE) || ct.includes('application/json')
        ? ((await res.json().catch(() => null)) as Partial<ProblemDetails> | null)
        : null;
    if (parsed && typeof parsed.type === 'string' && typeof parsed.status === 'number')
      throw new ApiError(parsed as ProblemDetails);
    throw new ApiError({
      type: `https://windrop.dev/problems/${res.status >= 500 ? 'provider_unavailable' : 'not_found'}`,
      title: res.statusText || 'Request failed',
      status: res.status,
    });
  }
  return (await res.json()) as T;
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

/** PUT the file to the presigned URL with progress; resolves when the storage layer accepted the bytes. */
export function uploadToPresignedUrl(
  url: string,
  headers: Record<string, string>,
  file: Blob,
  onProgress?: (p: UploadProgress) => void,
): Promise<void> {
  // Mock-mode seam (`?mock=1`, tests): the stateful mock issues `/mock/upload/*` URLs that need no transport.
  if (url.startsWith('/mock/upload/')) {
    onProgress?.({ loaded: file.size, total: file.size });
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url.startsWith('/') ? apiUrl(url) : url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.({ loaded: e.loaded, total: e.total });
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new NetworkError(`upload_failed_${xhr.status}`));
    xhr.onerror = () => reject(new NetworkError('upload_failed'));
    xhr.send(file);
  });
}
